import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../edit.module.scss';
import { formatDateTimeLabel, formatFileSize } from '../../../../utils/format';

interface Attachment {
    id?: string | number;
    name?: string;
    file_name?: string;
    filename?: string;
    original_name?: string;
    created_at?: string;
    createdAt?: string;
    size?: number;
    file_size?: number;
    removable?: boolean;
}

interface AttachmentsListProps {
    files?: Attachment[];
    heading?: ReactNode;
    className?: string;
    onRemove?: (file: Attachment) => void | Promise<void>;
}

const resolveName = (item: Attachment) =>
    item.name ?? item.file_name ?? item.filename ?? item.original_name ?? '';

const resolveCreatedDate = (item: Attachment) =>
    item.created_at ?? item.createdAt ?? null;

const resolveSize = (item: Attachment) => item.size ?? item.file_size ?? null;

export const AttachmentsList: FC<AttachmentsListProps> = ({
    files,
    heading,
    className,
    onRemove,
}) => {
    const { t } = useTranslation();
    const hasFiles = Array.isArray(files) && files.length > 0;

    return (
        <section className={className}>
            {heading && <h2 className={styles.sectionHeading}>{heading}</h2>}
            <div className={styles.attachmentsList}>
                {hasFiles ? (
                    files!.map(file => {
                        const name = resolveName(file);
                        const ext = name?.includes('.')
                            ? name.split('.').pop()?.toUpperCase()
                            : null;

                        return (
                            <div
                                key={file.id ?? name}
                                className={styles.attachmentRow}
                            >
                                <span className={styles.attachmentExt}></span>
                                <div className={styles.attachmentMeta}>
                                    <span className={styles.attachmentName}>
                                        {name}
                                    </span>
                                    <span className={styles.attachmentDetails}>
                                        {formatDateTimeLabel(
                                            resolveCreatedDate(file)
                                        )}
                                        {' • '}
                                        {formatFileSize(resolveSize(file))}
                                    </span>
                                </div>
                                {file.removable && onRemove && (
                                    <button
                                        type="button"
                                        className={styles.attachmentRemove}
                                        onClick={() => onRemove(file)}
                                        aria-label={t(
                                            'applications.wizard.remove_file',
                                            'Видалити файл'
                                        )}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className={styles.attachmentsEmpty}>
                        {t(
                            'applications.wizard.no_files',
                            'Файлів ще не завантажено'
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default AttachmentsList;
